import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuthStore, selectUser } from "../../store/auth.store.js";
import * as authApi from "../../api/auth.api.js";
import { Card, CardHeader, CardTitle, CardBody } from "../../components/ui/Card.jsx";
import { Input } from "../../components/ui/Input.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { Badge } from "../../components/ui/Badge.jsx";
import { formatDateTime } from "../../lib/format.js";

export default function ProfilePage() {
  const user = useAuthStore(selectUser);

  useEffect(() => {
    document.title = "Profile · Admin";
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-widest text-accent">You</p>
        <h1 className="mt-1 font-display text-2xl text-text-primary">Your profile</h1>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Account</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <Field label="Name" value={user?.name} />
            <Field label="Email" value={user?.email} />
            <Field
              label="Role"
              value={
                <Badge tone={user?.role === "super_admin" ? "accent" : "info"}>
                  {user?.role?.replace("_", " ")}
                </Badge>
              }
            />
            <Field
              label="Last login"
              value={user?.lastLoginAt ? formatDateTime(user.lastLoginAt) : "—"}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Change password</CardTitle>
          </CardHeader>
          <CardBody>
            <ChangePasswordForm />
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="grid grid-cols-[120px_1fr] items-center gap-3">
      <p className="text-xs uppercase tracking-wider text-text-muted">{label}</p>
      <div className="text-sm text-text-primary">{value ?? "—"}</div>
    </div>
  );
}

function ChangePasswordForm() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm();

  const change = useMutation({
    mutationFn: authApi.changePassword,
    onSuccess: () => {
      toast.success("Password changed — other sessions are now logged out.");
      reset();
    },
    onError: (err) => toast.error(err?.response?.data?.message ?? "Failed to change password"),
  });

  const submit = handleSubmit((values) => {
    if (values.newPassword !== values.confirmPassword) {
      toast.error("New passwords don't match.");
      return;
    }
    change.mutate({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    });
  });

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      <Input
        label="Current password"
        type="password"
        autoComplete="current-password"
        required
        error={errors.currentPassword?.message}
        {...register("currentPassword", { required: "Current password is required" })}
      />
      <Input
        label="New password"
        type="password"
        autoComplete="new-password"
        required
        hint="Min 8 characters."
        error={errors.newPassword?.message}
        {...register("newPassword", {
          required: "New password is required",
          minLength: { value: 8, message: "Min 8 characters" },
        })}
      />
      <Input
        label="Confirm new password"
        type="password"
        autoComplete="new-password"
        required
        error={errors.confirmPassword?.message}
        {...register("confirmPassword", { required: "Please confirm your password" })}
      />
      <Button type="submit" loading={isSubmitting || change.isPending}>
        Change password
      </Button>
    </form>
  );
}
